import api from '@/services/api';

const API_URL = '/gamme-config';

export interface GammeConfig {
    id: number;
    gamme: string;
    marque: string;
    unit: string;
    batch_size: number;
    prix_catalogue_ht: number;
    prix_vente_ttc: number;
    tva_rate: number;
    discount_mode: 'percentage' | 'free_units';
    payment_mode?: string;
    payment_term?: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface GammePricingTier {
    id: number;
    gamme_config_id: number;
    tier_number: number;
    min_quantity: number;
    max_quantity?: number;
    discount_percentage?: number;
    free_units?: number;
    payment_mode?: string;
    payment_term?: string;
    created_at: string;
    updated_at: string;
}

export interface GammeConfigCreate {
    gamme: string;
    marque: string;
    unit: string;
    batch_size: number;
    prix_catalogue_ht: number;
    prix_vente_ttc: number;
    tva_rate?: number;
    discount_mode: 'percentage' | 'free_units';
    payment_mode?: string;
    payment_term?: string;
}

export interface GammePricingTierCreate {
    tier_number: number;
    min_quantity: number;
    max_quantity?: number;
    discount_percentage?: number;
    free_units?: number;
    payment_mode?: string;
    payment_term?: string;
}

export interface PaymentTerm {
    id: number;
    label: string;
    value: string;
    position: number;
    is_default: boolean;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface PaymentTermCreate {
    label: string;
    value?: string;
    position?: number;
    is_default?: boolean;
}

export interface PaymentTermUpdate {
    label?: string;
    value?: string;
    position?: number;
    is_default?: boolean;
    is_active?: boolean;
}

export interface PriceCalculationResult {
    prix_client_ht: number;
    total_cost: number;
    ordered_units: number;
    free_units: number;
    total_received: number;
    effective_unit_price: number;
    tier_triggered: boolean;
    tier_number?: number;
    discount_percentage?: number;
    payment_mode?: string;
    payment_term?: string;
    display_text?: string;
    annotation?: string;
}

class GammeConfigService {
    // ==================== Gamme Config CRUD ====================

    async createGammeConfig(data: GammeConfigCreate): Promise<GammeConfig> {
        const response = await api.post(API_URL, data);
        return response.data;
    }

    async listGammeConfigs(isActive?: boolean): Promise<GammeConfig[]> {
        const params = isActive !== undefined ? { is_active: isActive } : {};
        const response = await api.get(API_URL, { params });
        return response.data;
    }

    async getGammeConfig(id: number): Promise<GammeConfig> {
        const response = await api.get(`${API_URL}/${id}`);
        return response.data;
    }

    async updateGammeConfig(id: number, data: Partial<GammeConfigCreate>): Promise<GammeConfig> {
        const response = await api.put(`${API_URL}/${id}`, data);
        return response.data;
    }

    async deleteGammeConfig(id: number): Promise<void> {
        await api.delete(`${API_URL}/${id}`);
    }

    // ==================== Pricing Tiers CRUD ====================

    async createPricingTier(configId: number, data: GammePricingTierCreate): Promise<GammePricingTier> {
        const response = await api.post(`${API_URL}/${configId}/tiers`, data);
        return response.data;
    }

    async listPricingTiers(configId: number): Promise<GammePricingTier[]> {
        const response = await api.get(`${API_URL}/${configId}/tiers`);
        return response.data;
    }

    async updatePricingTier(
        configId: number,
        tierId: number,
        data: Partial<GammePricingTierCreate>
    ): Promise<GammePricingTier> {
        const response = await api.put(`${API_URL}/${configId}/tiers/${tierId}`, data);
        return response.data;
    }

    async deletePricingTier(configId: number, tierId: number): Promise<void> {
        await api.delete(`${API_URL}/${configId}/tiers/${tierId}`);
    }

    // ==================== Price Calculation ====================

    async calculatePrice(configId: number, quantity: number): Promise<PriceCalculationResult> {
        const response = await api.post(`${API_URL}/${configId}/calculate-price`, null, {
            params: { quantity }
        });
        return response.data;
    }
    // ==================== Payment Terms CRUD ====================

    async listPaymentTerms(): Promise<PaymentTerm[]> {
        const response = await api.get(`${API_URL}/payment-terms/list`);
        return response.data;
    }

    async listAllPaymentTerms(): Promise<PaymentTerm[]> {
        const response = await api.get(`${API_URL}/payment-terms/all`);
        return response.data;
    }

    async createPaymentTerm(data: PaymentTermCreate): Promise<PaymentTerm> {
        const response = await api.post(`${API_URL}/payment-terms`, data);
        return response.data;
    }

    async updatePaymentTerm(id: number, data: PaymentTermUpdate): Promise<PaymentTerm> {
        const response = await api.put(`${API_URL}/payment-terms/${id}`, data);
        return response.data;
    }

    async deletePaymentTerm(id: number): Promise<void> {
        await api.delete(`${API_URL}/payment-terms/${id}`);
    }

    async seedPaymentTerms(): Promise<{ message: string }> {
        const response = await api.post(`${API_URL}/payment-terms/seed`);
        return response.data;
    }
}

export default new GammeConfigService();
